import {Modal} from '@dropbox/dig-components/modal';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {useEffect, useRef, useState} from 'react';

import {ContinueUpdatingModal} from './ContinueUpdatingModal';
import {useContinueUpdatingCandidates, useWorkstream} from './hooks';
import {
  WorkstreamUpdate,
  WorkstreamUpdateRef,
  WorkstreamUpdateSubmitResult,
} from './WorkstreamUpdate';

const MAX_WIDTH = 1600;

export const WorkstreamStatusUpdateModal = ({
  workstreamId,
  onClose,
  onSubmitted,
  source,
  statusOverride,
}: {
  workstreamId: number | undefined;
  onClose: () => void;
  onSubmitted?: (result: WorkstreamUpdateSubmitResult) => void;
  source: string;
  statusOverride?: string;
}) => {
  const [width, setWidth] = useState(MAX_WIDTH);
  const [activeWorkstreamId, setActiveWorkstreamId] = useState(workstreamId);
  const [activeStatusOverride, setActiveStatusOverride] = useState(statusOverride);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(Boolean(workstreamId));
  const [isContinueUpdatingOpen, setIsContinueUpdatingOpen] = useState(false);
  const workstreamUpdateRef = useRef<WorkstreamUpdateRef>(null);

  const {workstream} = useWorkstream(activeWorkstreamId);
  const continueUpdatingCandidates = useContinueUpdatingCandidates({
    excludeWorkstreamId: activeWorkstreamId ?? null,
    enabled: false,
  });

  useEffect(() => {
    const handleResize = () => {
      setWidth(Math.min(window.innerWidth - 160, MAX_WIDTH));
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setActiveWorkstreamId(workstreamId);
    setActiveStatusOverride(statusOverride);
    setIsUpdateModalOpen(Boolean(workstreamId));
    setIsContinueUpdatingOpen(false);
  }, [statusOverride, workstreamId]);

  const closeFlow = () => {
    setIsUpdateModalOpen(false);
    setIsContinueUpdatingOpen(false);
    onClose();
  };

  const logContinueUpdatingEvent = (
    event:
      | 'WORKSTREAM_CONTINUE_UPDATING_MODAL_SHOWN'
      | 'WORKSTREAM_CONTINUE_UPDATING_MODAL_DISMISSED'
      | 'WORKSTREAM_CONTINUE_UPDATING_CANDIDATE_SELECTED'
      | 'WORKSTREAM_CONTINUE_UPDATING_VIEW_MORE_CLICKED'
      | 'WORKSTREAM_CONTINUE_UPDATING_NO_CANDIDATES'
      | 'WORKSTREAM_CONTINUE_UPDATING_FETCH_FAILED',
    data?: Record<string, boolean | number | string | undefined | string[]>
  ) => {
    analyticsLogger().logEvent(event, {
      source,
      workstream_id: activeWorkstreamId,
      ...data,
    });
  };

  const handleClose = () => {
    if (workstreamUpdateRef.current?.isSubmissionInProgress()) {
      return;
    }

    // Check for unsaved changes before closing
    const canClose = workstreamUpdateRef.current?.confirmUnsavedChanges() ?? true;
    if (canClose) {
      closeFlow();
    }
  };

  const handleAfterSubmit = async (result?: WorkstreamUpdateSubmitResult) => {
    if (result) {
      onSubmitted?.(result);
    }

    if (!result) {
      closeFlow();
      return;
    }

    try {
      const response = await continueUpdatingCandidates.refetch();
      const pages = response.data?.pages ?? [];
      const candidateCount = pages.reduce((total, page) => total + page.items.length, 0);
      const hasCandidates = candidateCount > 0;
      const hasNextPage = Boolean(pages[pages.length - 1]?.next_cursor);

      if (!hasCandidates) {
        logContinueUpdatingEvent('WORKSTREAM_CONTINUE_UPDATING_NO_CANDIDATES', {
          submitted_status: result.status,
        });
        closeFlow();
        return;
      }

      logContinueUpdatingEvent('WORKSTREAM_CONTINUE_UPDATING_MODAL_SHOWN', {
        candidate_count: candidateCount,
        has_next_page: hasNextPage,
        submitted_status: result.status,
      });
      setIsUpdateModalOpen(false);
      setIsContinueUpdatingOpen(true);
    } catch {
      logContinueUpdatingEvent('WORKSTREAM_CONTINUE_UPDATING_FETCH_FAILED', {
        submitted_status: result.status,
      });
      closeFlow();
    }
  };

  const handleSelectContinueUpdatingCandidate = ({
    id,
    latest_update_status,
  }: {
    id: number;
    latest_update_status?: string | null;
  }) => {
    logContinueUpdatingEvent('WORKSTREAM_CONTINUE_UPDATING_CANDIDATE_SELECTED', {
      selected_workstream_id: id,
      latest_update_status: latest_update_status ?? undefined,
    });
    setActiveWorkstreamId(id);
    setActiveStatusOverride(undefined);
    setIsContinueUpdatingOpen(false);
    setIsUpdateModalOpen(true);
  };

  const handleViewMore = () => {
    logContinueUpdatingEvent('WORKSTREAM_CONTINUE_UPDATING_VIEW_MORE_CLICKED', {
      candidate_count: continueUpdatingCandidates.candidates.length,
    });
    void continueUpdatingCandidates.fetchNextPage();
  };

  const handleCloseContinueUpdating = () => {
    logContinueUpdatingEvent('WORKSTREAM_CONTINUE_UPDATING_MODAL_DISMISSED', {
      candidate_count: continueUpdatingCandidates.candidates.length,
    });
    closeFlow();
  };

  const isWorkstreamUpdateModalOpen = Boolean(activeWorkstreamId) && isUpdateModalOpen;

  return (
    <>
      <Modal
        open={isWorkstreamUpdateModalOpen}
        onRequestClose={handleClose}
        width={width}
        withCloseButton="Close"
      >
        {workstream && isWorkstreamUpdateModalOpen && (
          <WorkstreamUpdate
            ref={workstreamUpdateRef}
            workstream={workstream}
            onAfterSubmit={handleAfterSubmit}
            source={source}
            statusOverride={activeStatusOverride}
          />
        )}
      </Modal>
      <ContinueUpdatingModal
        open={isContinueUpdatingOpen}
        candidates={continueUpdatingCandidates.candidates}
        hasNextPage={Boolean(continueUpdatingCandidates.hasNextPage)}
        isFetchingNextPage={continueUpdatingCandidates.isFetchingNextPage}
        onClose={handleCloseContinueUpdating}
        onSelectCandidate={handleSelectContinueUpdatingCandidate}
        onViewMore={handleViewMore}
      />
    </>
  );
};
